import { useCallback } from "react";
import { toast } from "react-toastify";

import { NavBarSearchKeys } from "components/NavBarSearch/queryKeys";
import { useClaireAppointmentSearchQuery } from "components/NavBarSearchAppointments/hooks/useClaireAppointmentSearchQuery";
import { useDmsAppointmentSearchQuery } from "components/NavBarSearchAppointments/hooks/useDmsAppointmentSearchQuery";
import { typedQueryClient } from "hooks/useTypedQuery";
import { DMS_CAPABILITIES } from "models";

import type { AppointmentSearchOption, SearchResultsData } from "components/NavBarSearchAppointments/util";
import type { Dealer, DealerLocation } from "models";

const formatSearchResults = (searchOptions: AppointmentSearchOption[], selectedLocationId?: number) => {
  const searchResults: SearchResultsData = { currentLocation: [], otherLocations: [] };

  searchOptions.forEach(appointment => {
    const isCurrentLocation = !appointment.dealer_location || appointment.dealer_location_id === selectedLocationId;
    if (isCurrentLocation) searchResults.currentLocation.push(appointment);
    else searchResults.otherLocations.push(appointment);
  });

  return searchResults;
};

const mergeSearchResults = (claireOptions: AppointmentSearchOption[] = [], dmsOptions: AppointmentSearchOption[] = [], selectedLocationId?: number) => {
  const importedDmsNumbers = claireOptions.reduce((dmsNumbers, claireOption) => {
    if (claireOption.dealer_location_id === selectedLocationId) dmsNumbers.add(claireOption.dms_nr);
    return dmsNumbers;
  }, new Set<string>());

  const notImportedDmsOptions = dmsOptions.filter(dmsOption => !importedDmsNumbers.has(dmsOption.dms_nr));

  return formatSearchResults([...claireOptions, ...notImportedDmsOptions], selectedLocationId);
};

export const useAppointmentSearchResults = (searchTerm: string, selectedLocation?: DealerLocation | null, selectedDealer?: Dealer | null) => {
  const canSearchDms = !!selectedLocation?.dms_capability_ids?.includes(DMS_CAPABILITIES.AppointmentSearch);

  const {
    data: claireOptions,
    refetch: refetchClaireAppointments,
    isFetching: isFetchingClaireAppointments,
    error
  } = useClaireAppointmentSearchQuery(searchTerm, selectedDealer);
  const { data: dmsOptions, refetch: refetchDmsAppointments, isFetching: isFetchingDmsAppointments } = useDmsAppointmentSearchQuery(searchTerm, selectedLocation);

  if (error) toast.error(error.message);

  const isFetchingAppointments = isFetchingClaireAppointments || isFetchingDmsAppointments;
  const hasSearched = !!claireOptions || !!dmsOptions || isFetchingAppointments;
  const isWaitingForClaire = !claireOptions && isFetchingClaireAppointments;
  const dmsOptionsToShow = isWaitingForClaire ? [] : dmsOptions;
  const searchResults = hasSearched ? mergeSearchResults(claireOptions, dmsOptionsToShow, selectedLocation?.id) : undefined;

  const refetch = useCallback(() => {
    refetchClaireAppointments();
    if (canSearchDms) refetchDmsAppointments();
  }, [refetchClaireAppointments, refetchDmsAppointments, canSearchDms]);

  const reset = () => {
    typedQueryClient.resetQueries(NavBarSearchKeys.claireAppointmentSearchResults);
    typedQueryClient.resetQueries(NavBarSearchKeys.dmsAppointmentSearchResults);
  };

  return { searchResults, isFetchingAppointments, refetch, reset };
};
